import React from "react";
import { BoxProps } from "@mui/material";
import {
  BoundNutrientsRender,
  BoundNutritionInputProps,
} from "./BoundNutrientsRender";

export interface NutritionInputProps {
  value: number;
  setValue: (value: number) => void;
  dirty: boolean;
  setDirty: (dirty: boolean) => void;
  targetValue?: number;
}

export interface BoundNutrientsProps extends BoxProps {
  protein: NutritionInputProps;
  carbohydrate: NutritionInputProps;
  fat: NutritionInputProps;
  calories: NutritionInputProps;
  variant: "target" | "entry";
}

const MAX_NUTRIENT_VALUE = 3999;

const MAX_CALORIES_VALUE =
  // fat
  MAX_NUTRIENT_VALUE * 9 +
  // protein, carbohydrate
  MAX_NUTRIENT_VALUE * 4 * 2;

export function BoundNutrients(props: BoundNutrientsProps) {
  const {
    protein: proteinRaw,
    carbohydrate: carbohydrateRaw,
    fat: fatRaw,
    calories: caloriesRaw,
    variant,
  } = props;

  const {
    value: proteinValue,
    setValue: setProteinValue,
    dirty: proteinDirty,
    setDirty: setProteinDirty,
  } = proteinRaw;
  const {
    value: carbohydrateValue,
    setValue: setCarbohydrateValue,
    dirty: carbohydrateDirty,
    setDirty: setCarbohydrateDirty,
  } = carbohydrateRaw;
  const {
    value: fatValue,
    setValue: setFatValue,
    dirty: fatDirty,
    setDirty: setFatDirty,
  } = fatRaw;
  const {
    value: caloriesValue,
    setValue: setCaloriesValue,
    dirty: caloriesDirty,
    setDirty: setCaloriesDirty,
  } = caloriesRaw;

  const validFloat = (value: number) => {
    return (
      value >= 0 && value !== -Infinity && value !== Infinity && !isNaN(value)
    );
  };

  const validateInputNumber = (
    value: number,
    prevValue: number,
    defaultValue = 0,
    maxValue = 99999,
  ) => {
    if (value < 0) {
      return defaultValue;
    } else if (value > maxValue) {
      return prevValue;
    } else if (isNaN(value)) {
      return defaultValue;
    } else return value;
  };

  const calculateCalories = (
    fatValue: number,
    proteinValue: number,
    carbohydrateValue: number,
  ): number => fatValue * 9 + proteinValue * 4 + carbohydrateValue * 4;

  const handleChangeProtein = React.useCallback(
    (value: number) => {
      setProteinDirty(true);
      const newProtein = validateInputNumber(
        value,
        proteinValue,
        0,
        MAX_NUTRIENT_VALUE,
      );
      const newCaloriesValue = calculateCalories(
        fatValue,
        newProtein,
        carbohydrateValue,
      );
      setCaloriesValue(newCaloriesValue);
      setProteinValue(newProtein);
    },
    [fatValue, proteinValue, carbohydrateValue],
  );

  const handleChangeCarbohydrate = React.useCallback(
    (value: number) => {
      setCarbohydrateDirty(true);
      const newCarbohydrate = validateInputNumber(
        value,
        carbohydrateValue,
        0,
        MAX_NUTRIENT_VALUE,
      );
      const newCaloriesValue = calculateCalories(
        fatValue,
        proteinValue,
        newCarbohydrate,
      );
      setCaloriesValue(newCaloriesValue);
      setCarbohydrateValue(newCarbohydrate);
    },
    [fatValue, proteinValue, carbohydrateValue],
  );

  const handleChangeFat = React.useCallback(
    (value: number) => {
      setFatDirty(true);
      const newFat = validateInputNumber(
        value,
        fatValue,
        0,
        MAX_NUTRIENT_VALUE,
      );
      const newCaloriesValue = calculateCalories(
        newFat,
        proteinValue,
        carbohydrateValue,
      );
      setCaloriesValue(newCaloriesValue);
      setFatValue(newFat);
    },
    [fatValue, proteinValue, carbohydrateValue],
  );

  const handleChangeCalories = React.useCallback(
    (value: number) => {
      const finiteSegment = (value: number) => (validFloat(value) ? value : 0);

      const fatSegment = finiteSegment((fatValue * 9) / caloriesValue);
      const proteinSegment = finiteSegment((proteinValue * 4) / caloriesValue);
      const carbohydrateSegment = finiteSegment(
        (carbohydrateValue * 4) / caloriesValue,
      );

      const validateSum = fatSegment + proteinSegment + carbohydrateSegment;

      const validFatSegment = finiteSegment(fatSegment / validateSum);
      const validProteinSegment = finiteSegment(proteinSegment / validateSum);
      const validCarbohydrateSegment = finiteSegment(
        carbohydrateSegment / validateSum,
      );

      const newCaloriesValue = validateInputNumber(
        value,
        caloriesValue,
        0,
        MAX_CALORIES_VALUE,
      );

      const newFatValue = (validFatSegment * newCaloriesValue) / 9;
      const newProteinValue = (validProteinSegment * newCaloriesValue) / 4;
      const newCarbohydrateValue =
        (validCarbohydrateSegment * newCaloriesValue) / 4;

      setCaloriesDirty(true);
      setCaloriesValue(newCaloriesValue);

      if (
        (!validFloat(newFatValue) || newFatValue === 0) &&
        (!validFloat(newProteinValue) || newProteinValue === 0) &&
        (!validFloat(newCarbohydrateValue) || newCarbohydrateValue === 0) &&
        newCaloriesValue > 0
      ) {
        setFatValue((newCaloriesValue * 0.3) / 9);
        setProteinValue((newCaloriesValue * 0.3) / 4);
        setCarbohydrateValue((newCaloriesValue * 0.4) / 4);
      } else {
        setFatValue(newFatValue);
        setProteinValue(newProteinValue);
        setCarbohydrateValue(newCarbohydrateValue);
      }
    },
    [caloriesValue, fatValue, carbohydrateValue, proteinValue],
  );

  const protein: BoundNutritionInputProps = {
    ...proteinRaw,
    onChange: handleChangeProtein,
  };

  const carbohydrate: BoundNutritionInputProps = {
    ...carbohydrateRaw,
    onChange: handleChangeCarbohydrate,
  };

  const fat: BoundNutritionInputProps = {
    ...fatRaw,
    onChange: handleChangeFat,
  };

  const calories: BoundNutritionInputProps = {
    ...caloriesRaw,
    onChange: handleChangeCalories,
  };

  return (
    <BoundNutrientsRender
      protein={protein}
      carbohydrate={carbohydrate}
      fat={fat}
      calories={calories}
      adormentVariant={
        variant === "target" ? "distribution" : "target-overflow"
      }
    />
  );
}
